// Adapted from eval/dashboard/internal_web/src/components/session/FinalDifferential.tsx (ATM-57): no ground truth, no probability bars, reasoning in its own column, the report findings under it. Keep in step until ATM-60 shares it.
/**
 * What the consultation ended on: the report's ranked differentials — the eval dashboard's
 * `FinalDifferential`, without its ground-truth marks — and, under them, the report's findings.
 * Jiva's belief mid-consult, answer by answer, is the panel beside the transcript; this is the
 * answer it gave.
 *
 * The urgency column and the "Urgency set by" line follow the one rule the session's urgency is
 * derived by (`lib/urgency`): the highest risk across the top three entries, so entries below
 * the window recede — they cannot have set it.
 *
 * A report exists only once a session completes, so while it runs the panel says so; a
 * completed session with no differentials says it loudly, because that is the most important
 * fact about it.
 */

import { Target } from "lucide-react";

import type {
  AdminReportView,
  AdminSessionDetail,
  FindingItem,
  ReportDifferential,
} from "../../api/types";
import { calendarDate, clockTime, EM_DASH, pctBounded } from "../../lib/format";
import { riskUrgency, TRIAGE_WINDOW, urgencyDriver } from "../../lib/urgency";
import Acuity from "../ui/Acuity";
import { reportProbabilities } from "./reportProbability";
import { CardPanel } from "../ui/Card";
import NullNote from "../ui/NullNote";

function nameOf(entry: ReportDifferential): string {
  return entry.condition ?? entry.name ?? "Unnamed";
}

/** Why a session has no report, in words. */
function missingReport(status: AdminSessionDetail["status"]): string {
  if (status === "active") {
    return "The final differential is composed when the consultation completes.";
  }
  if (status === "abandoned") {
    return "No final differential — the session ended before the consultation completed.";
  }
  return "This session completed without a report.";
}

/** Which entry set the session's urgency, in one line. */
function DriverNote({
  entries,
  probabilities,
}: {
  entries: readonly ReportDifferential[];
  probabilities: readonly (number | null)[];
}) {
  const driver = urgencyDriver(entries);
  if (driver === null) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-panel border border-hairline-2 px-4 py-3 text-body-sm text-ink">
      <Target size={14} className="text-ink-faint" aria-hidden />
      <span className="text-ink-3">Urgency set by</span>
      <span>
        #{driver.rank} {nameOf(driver.entry)}
      </span>
      <span className="text-ink-3">at</span>
      <span className="tabular-nums">{pctBounded(probabilities[driver.rank - 1] ?? null)}</span>
      <span className="text-ink-3" aria-hidden>
        →
      </span>
      <Acuity urgency={driver.urgency} />
    </p>
  );
}

/**
 * The ranked entries. From `lg` the reasoning takes a column of its own beside the diagnosis,
 * so a row is as tall as its reasoning wraps across the width rather than three times that in
 * a narrow strip; below `lg` it drops back under the name.
 */
function DifferentialTable({
  entries,
  probabilities,
}: {
  entries: readonly ReportDifferential[];
  probabilities: readonly (number | null)[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse">
        <caption className="sr-only">The report&apos;s ranked differentials</caption>
        <thead>
          <tr>
            <th scope="col" className="table-head w-10">
              #
            </th>
            <th scope="col" className="table-head pr-6 lg:w-[16rem]">
              Diagnosis
            </th>
            <th scope="col" className="table-head hidden pr-8 lg:table-cell">
              Reasoning
            </th>
            <th scope="col" className="table-head w-[7rem] pr-6 text-right">
              Probability
            </th>
            <th scope="col" className="table-head w-[9rem]">
              Urgency
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, position) => {
            const rank = position + 1;
            const probability = probabilities[position] ?? null;
            const reasoning = entry.reasoning?.trim() || entry.brief_reason?.trim() || "";
            const urgency = riskUrgency(entry);
            const reasoningText =
              reasoning !== "" ? (
                reasoning
              ) : (
                <span className="text-ink-faint">No reasoning recorded</span>
              );
            return (
              <tr key={position} className="hairline">
                <td className="table-cell align-top text-ink-faint">{rank}</td>
                <td className="table-cell pr-6 align-top">
                  {/* Entries below the window stay legible but recede. */}
                  <span className={rank > TRIAGE_WINDOW ? "text-ink-3" : "text-ink"}>
                    {nameOf(entry)}
                  </span>
                  <span className="mt-1 block whitespace-pre-line text-caption font-normal text-ink-3 lg:hidden">
                    {reasoningText}
                  </span>
                </td>
                <td className="table-cell hidden whitespace-pre-line pr-8 align-top text-caption font-normal text-ink-3 lg:table-cell">
                  {reasoningText}
                </td>
                <td className="table-cell pr-6 text-right align-top tabular-nums">
                  {pctBounded(probability)}
                </td>
                <td className="table-cell align-top">
                  {urgency !== null ? (
                    <Acuity urgency={urgency} />
                  ) : (
                    <span className="text-cell text-ink-3" title="This entry carried no risk level.">
                      {EM_DASH} not recorded
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One of the report's three finding lists. */
function FindingsList({ label, items }: { label: string; items: readonly FindingItem[] }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <h5 className="text-cell font-medium text-ink-3">{label}</h5>
      {items.length === 0 ? (
        <p className="text-body-sm text-ink-faint">None recorded</p>
      ) : (
        <ul className="flex list-disc flex-col gap-1 pl-5 text-body-sm text-ink">
          {items.map((item, position) => (
            <li key={position}>{item.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The report's findings, three lists side by side from `lg`, stacked below it. */
function Findings({ report }: { report: AdminReportView }) {
  return (
    <div className="hairline flex flex-col gap-3 pt-4">
      <h4 className="text-panel text-ink">Findings</h4>
      <div className="grid gap-x-8 gap-y-5 lg:grid-cols-3">
        <FindingsList label="Key positives" items={report.key_positives} />
        <FindingsList label="Key negatives" items={report.key_negatives} />
        <FindingsList label="Risk factors" items={report.risk_factors} />
      </div>
    </div>
  );
}

export default function FinalDifferential({ detail }: { detail: AdminSessionDetail }) {
  const { report } = detail;
  const entries = report?.working_differentials ?? [];
  const probabilities = reportProbabilities(entries);

  return (
    <CardPanel className="flex flex-col gap-4 px-5 pb-5 pt-4">
      <div>
        <h3 className="text-panel text-ink">The final differential</h3>
        <p className="mt-0.5 text-caption text-ink-3">
          What Jiva&apos;s report committed to at the end of the consultation. The panel beside
          the transcript is Jiva&apos;s belief mid-consult; this is the answer it gave.
          {report !== null
            ? ` Composed ${calendarDate(report.created_at)} at ${clockTime(report.created_at)}.`
            : ""}
        </p>
      </div>

      {report === null ? (
        <NullNote>{missingReport(detail.status)}</NullNote>
      ) : (
        <>
          {entries.length === 0 ? (
            <div className="note-critical px-4 py-3" role="alert">
              <p className="text-body text-ink">This report carries no differentials.</p>
            </div>
          ) : (
            <>
              <DriverNote entries={entries} probabilities={probabilities} />
              <DifferentialTable entries={entries} probabilities={probabilities} />
              {entries.length > TRIAGE_WINDOW ? (
                <p className="text-caption text-ink-faint">
                  Entries below #{TRIAGE_WINDOW} recede: the session&apos;s urgency is derived
                  from the top {TRIAGE_WINDOW} only, so they cannot have set it.
                </p>
              ) : null}
            </>
          )}
          <Findings report={report} />
        </>
      )}
    </CardPanel>
  );
}
